import React, { useEffect, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { getValue } from 'remote-config-value'
import { makeStyles } from '@material-ui/core/styles'
import { minlength, regex, required, url } from 'validation-rules'
import { Select, TextField, Typography, Infotip } from 'foundations-library/components'
import MessageFlow from './MessageFlow'
import StepTitle from '../../StepTitle'
import InfoSection from '../../InfoSection'
import useFlatForm from '../../../hooks/useFlatForm'
import UseCasesCheckboxes from './UseCasesCheckboxes'
import BrandsContext from '../../../contexts/BrandsContext'
import useDisableInput from '../../../hooks/useDisableInput'
import { useCasesOptions } from '../../../utils/util'

const useStyles = makeStyles((theme) => {
    const { colors } = theme['foundations-library']
    return {
        container: {
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: 30
        },
        redAsterisk: {
            color: colors.destructive[500]
        },
        infotip: {
            maxWidth: 253
        }
    }
})

const getConsentIntoInitialValue = (wizardData, editing) => {
    if (wizardData?.consentInfo) return wizardData.consentInfo
    if (!editing) return {}
    const cInfo = editing.consent_info || editing.consentInfo
    if (typeof cInfo === 'string') {
        return JSON.parse(cInfo)
        // try {
        // } catch {
        //     const fixedString = cInfo.replace(/'/g, '"')
        //     return JSON.parse(fixedString)
        // }
    }

    return cInfo
}

const getUsecases = (wizardData, editing) => {
    if (wizardData?.useCases) return wizardData.useCases
    if (!editing) return []

    let useCases = useCasesOptions.filter((x) => x.backendProp && editing[x.backendProp]).map((x) => x.id)
    if (editing.uc_other) useCases.push(editing.uc_other)
    useCases = useCases.filter(Boolean)
    if (!useCases.length && editing.use_cases.length) {
        useCases = editing.use_cases
    }
    return [...(new Set(useCases.filter(Boolean)))]
}

/**
 * @param {object} props
 */
const Usage = ({
    editing,
    ...props
}) => {
    const campaignUsageSavedData = props?.passData?.campaign_usage

    const { verifiedBrands } = useContext(BrandsContext)
    const {
        data,
        onClear,
        setDataProp,
        setIsValidProp,
        isFormDataValid,
        setMultipleIsValidProps,
        setMultipleDataProps
    } = useFlatForm({
        selectedBrand: campaignUsageSavedData?.selectedBrand || (verifiedBrands.length === 1 ? verifiedBrands[0] : (editing != null ? verifiedBrands.find(x => x.csp_brand_id === editing.csp_brand_id) : null)),
        campaignName: campaignUsageSavedData?.campaignName || (editing != null ? editing.name : ''),
        useCases: getUsecases(campaignUsageSavedData, editing),
        privacyPolicyLink: campaignUsageSavedData?.privacyPolicyLink || (editing != null ? editing.privacy_policy_url : ''),
        messageFlow: campaignUsageSavedData?.messageFlow || (editing != null ? editing.messageflow : ''),
        consentInfo: getConsentIntoInitialValue(campaignUsageSavedData, editing),
        files: campaignUsageSavedData?.files || (editing != null ? editing.documents : [])
    })

    const {
        disableAllInputs,
        disableOnEditing
    } = useDisableInput({ mode: props.mode, item: editing })

    const classes = useStyles()

    useEffect(() => {
        if (props.saveId) props.update?.({ completed: true, gotoNext: true, saveData: data })
    }, [props.saveId])

    useEffect(() => {
        props.update?.({ disableNextButton: () => !isFormDataValid })
    }, [isFormDataValid])

    const onBrandChange = (v) => {
        setDataProp('selectedBrand')(verifiedBrands.find((x) => x.csp_brand_id === v))
    }

    const infotipTextValue = useMemo(() => {
        if (disableOnEditing && props.mode === 'editing') {
            return 'You cannot edit this field, to change brand you will have to start a new campaign application.'
        }

        return ''
    }, [])

    return (
        <div className={classes.container}>
            <StepTitle
                remoteConfigID='sms_enrollment_campaigns_1st_step_title'
                status={editing?.status}
                mode={props.mode}
                onClickEdit={props.onClickEdit}
                onClickSubmitPaymentFailed={props.onClickSubmitPaymentFailed}
            />
            <div className={classes.form}>
                <Infotip value={infotipTextValue} bottom className={classes.infotip}>
                    <Select
                        id='brandAutocomplete'
                        onChange={onBrandChange}
                        value={data.selectedBrand?.csp_brand_id}
                        options={verifiedBrands
                            .map((b) => ({ text: b.name, value: b.csp_brand_id }))
                        }
                        disabled={disableAllInputs || disableOnEditing}
                        isValid={setIsValidProp('selectedBrand')}
                        onClear={onClear('selectedBrand')}
                        rules={[required()]}
                        label='Brand'
                    />
                </Infotip>
                <TextField
                    id='campaignNameTxtArea'
                    value={data.campaignName}
                    label={getValue('sms_enrollment_campaign_name_placeholder')}
                    onChange={setDataProp('campaignName')}
                    onClear={onClear('campaignName')}
                    isValid={setIsValidProp('campaignName')}
                    rules={[required(), minlength(3), regex(new RegExp(/^[a-zA-Z0-9! ]+$/g))]}
                    disabled={disableAllInputs}
                />
                <div>
                    <Typography remoteConfigID='sms_enrollment_campaigns_1st_step_checkboxes_label' tag={'span'} /><span className={classes.redAsterisk}>*</span>
                    <Infotip value={infotipTextValue} bottom className={classes.infotip}>
                        <UseCasesCheckboxes
                            value={data.useCases}
                            onChange={setDataProp('useCases')}
                            disabled={disableAllInputs || disableOnEditing}
                            isValid={setIsValidProp('useCases')}
                        />
                    </Infotip>
                </div>
                <MessageFlow
                    initialValue={{
                        messageFlow: data.messageFlow,
                        consentInfo: data.consentInfo,
                        files: data.files
                    }}
                    onChange={setMultipleDataProps}
                    isValid={setMultipleIsValidProps}
                    disabled={disableAllInputs}
                    item={editing}
                />
                <TextField
                    type='url'
                    value={data.privacyPolicyLink}
                    onChange={setDataProp('privacyPolicyLink')}
                    label={getValue('sms_enrollment_campaigns_1st_link_to_privacy_policy')}
                    helperText={getValue('sms_enrollment_campaigns_1st_link_to_privacy_policy_help_text')}
                    isValid={setIsValidProp('privacyPolicyLink')}
                    rules={[required(), url()]}
                    onClear={onClear('privacyPolicyLink')}
                    disabled={disableAllInputs}
                />
                <InfoSection>
                    <div>{getValue('sms_enrollment_campaigns_1st_step_info_title')}</div>
                    <br />
                    <ul>
                        {(JSON.parse(getValue('sms_enrollment_campaigns_1st_step_info_list'))).map((x) => <li key={x}>{x}</li>)}
                    </ul>
                </InfoSection>
            </div>
        </div>
    )
}

Usage.propTypes = {
    passData: PropTypes.object,
    update: PropTypes.func,
    saveId: PropTypes.number,
    editing: PropTypes.object,
    preview: PropTypes.object,
    showContainer: PropTypes.bool,
    showTitle: PropTypes.bool,
    mode: PropTypes.string,
    onClickEdit: PropTypes.func,
    onClickSubmitPaymentFailed: PropTypes.func
}

export default Usage
